<template>
  <UPopover ref="popoverRef" :open="showConfirm" @update:open="val => showConfirm = val">
    <!-- Toggle button always visible -->
    <UButton
      type="button"
      variant="link"
      class="inline-flex items-center gap-2 group select-none"
      :class="[btnClass, inheritedButtonClass]"
      v-bind="buttonAttrs"
      @click="handleToggle"
      :aria-pressed="isSaved"
      :aria-disabled="disabled"
      :disabled="disabled"
      @mouseenter="hovered = true"
      @mouseleave="hovered = false"
    >  
      <span v-if="showLabel" class="text-left text-xs whitespace-nowrap">
        {{ isSaved ? savedLabel : saveLabel }}
      </span>    
      <span
        class="inline-flex items-center justify-center border border-black shrink-0 overflow-hidden transition-colors duration-300 group-focus-visible:outline group-focus-visible:outline-2 group-focus-visible:outline-black group-focus-visible:outline-offset-2"
        :class="ringClass"
      >
        <UiCheck class="w-3" :checked="isSaved || hovered"/>
        
      </span>
    </UButton>

    <!-- Only show popover content when UNSAVING -->
    <template #content v-if="pendingUnsave">
      <div class="p-4">
        <h3 class="w-full text-center mb-2">Are you sure?</h3>
        <p class="text-sm mb-2">This will remove the model including QTY's.</p>
        <div class="flex gap-2">
          <UButton :block="true"  size="sm" color="primary" @click="onConfirmUnsave">Yes</UButton>
          <UButton :block="true" size="sm" variant="outline" @click="closePopover">No</UButton>
        </div>
      </div>
    </template>
  </UPopover>

</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch, useAttrs } from 'vue'
import type { InstanceType } from 'vue'
import type { ProductModel } from '@/types/catalog'
import { useCartStore } from '@/store/cart'

defineOptions({ inheritAttrs: false })
const attrs = useAttrs()
const buttonAttrs = computed(() => {
  const { class: _class, ...rest } = attrs
  return rest
})
const inheritedButtonClass = computed(() => attrs.class)
const toast = useToast()
const popoverRef = ref<InstanceType<typeof UPopover> | null>(null)

const props = withDefaults(defineProps<{
  model: ProductModel
  size?: 'md' | 'sm'
  disabled?: boolean
  saveLabel?: string
  savedLabel?: string
  showLabel?: boolean
  disableInitialAnimate?: boolean
  shape?: 'round' | 'square'
}>(), {
  size: 'md',
  disabled: false,
  saveLabel: 'Save',
  savedLabel: 'Saved',
  showLabel: true,
  disableInitialAnimate: true,
  shape: 'round'
})

const cart = useCartStore()
onMounted(() => cart.hydrate())

const hovered = ref(false)

const isSaved = computed(() =>
  props.model?.Style ? cart.modelIsSaved(props.model.Style) : false
)

const btnClass = computed(() => [
  props.size === 'sm' ? 'text-[12px]' : 'text-xs',
  props.disabled ? 'opacity-50 pointer-events-none' : ''
].join(' '))

const ringClass = computed(() => [
  props.size === 'sm' ? 'h-6 w-6' : 'h-6 w-6',
  props.shape === 'square' ? 'rounded-[2px]' : 'rounded-full',
  isSaved.value ? 'bg-black text-white' : 'bg-transparent',
  hovered.value && isSaved.value === false ? 'bg-workbook-dark-grey border-workbook-dark-grey' : ''
].join(' '))

const mounted = ref(false)
onMounted(() => requestAnimationFrame(() => { mounted.value = true }))

const showConfirm = ref(false)
const pendingUnsave = ref(false)

// UNDO STATE
const lastRemoved = ref<{
  style: string | number
  quantities: Record<string, number>
} | null>(null)

/**
 * Kontrollera om modellen har några kvantiteter > 0
 */
function modelHasAnyQty(model: ProductModel): boolean {
  if (!model) return false
  for (const v of model.variants) {
    if (!v.SKU) continue
    const li = cart.lineItems[v.SKU]
    if (li && li.qty > 0) return true
  }
  return false
}

/**
 * Only show popover when user is UNSAVING (unchecking)
 */
function handleToggle() {
  if (props.disabled) return
  if (isSaved.value) {
    // Om modellen har kvantiteter, visa popover.
    if (modelHasAnyQty(props.model)) {
      showConfirm.value = true
      pendingUnsave.value = true
    } else {
      // Om inga kvantiteter, ta bort direkt utan popover.
      performUnsave()
    }
  } else {
    // Save directly, do NOT show popover
    cart.toggleModelSave(props.model)
    sendWisepopsAddToCartEvent()
    showConfirm.value = false
    pendingUnsave.value = false
  }
}

/**
 * Core unsave-action, sparar för undo!
 */
function performUnsave() {
  // Spara nuvarande kvantiteter för undo
  const quantities: Record<string, number> = {}
  for (const v of props.model.variants) {
    if (!v.SKU) continue
    const li = cart.lineItems[v.SKU]
    quantities[v.SKU] = li?.qty || 0
    if (li) li.qty = 0
  }
  lastRemoved.value = {
    style: props.model.Style,
    quantities
  }
  cart.toggleModelSave(props.model)
  cart.persist()
  showConfirm.value = false
  pendingUnsave.value = false
  toast.add({
    title: 'Model/Style removed',
    description: 'All quantities for this model have been cleared',
    actions: [{
      icon: 'i-lucide-undo',
      label: 'Undo',
      color: 'neutral',
      variant: 'outline',
      onClick: (e) => {
        e?.stopPropagation()
        undoRemove()
      }
    }]
  })
}

/**
 * When user clicks "Yes" in popover
 */
function onConfirmUnsave() {
  performUnsave()
}

/**
 * Undo-funktion: återställ senaste borttagna modell och dess kvantiteter
 */
function undoRemove() {
  const last = lastRemoved.value
  if (!last) return
  // Återställ savedStyles
  cart.addSavedStyle(last.style)
  // Återställ kvantiteter
  for (const [sku, qty] of Object.entries(last.quantities)) {
    if (cart.lineItems[sku]) {
      cart.lineItems[sku].qty = qty
    } else {
      // Om SKU saknas, skapa rad
      cart.lineItems[sku] = {
        sku,
        style: last.style,
        colour: '', // Färg är okänd här, kan hämtas om nödvändigt
        size: '',
        qty
      }
    }
  }
  cart.persist()
  toast.add({
    title: 'Undo successful',
    description: 'Model and all quantities have been restored'
  })
  lastRemoved.value = null
}

/**
 * When user clicks "No" in popover
 */
function closePopover() {
  showConfirm.value = false
  pendingUnsave.value = false
}

// Extra: If user saves/unsaves outside, always close popover
watch(isSaved, (newVal) => {
  if (!newVal) {
    showConfirm.value = false
    pendingUnsave.value = false
  }
})

function sendWisepopsAddToCartEvent() {
  if (typeof window === 'undefined') return
  const windowWithWisepops = window as typeof window & { wisepops?: ((...args: unknown[]) => void) }
  windowWithWisepops.wisepops?.('event', 'addToCart')
}
</script>
